/**
 * ضغط الصورة قبل الرفع
 * @param {File} file - ملف الصورة الأصلي
 * @param {Object} options - خيارات الضغط
 * @returns {Promise<File>} - ملف الصورة المضغوط
 */
export const compressImage = async (
  file,
  options = {
    maxWidth: 1920,
    maxHeight: 1920,
    quality: 0.8,
    mimeType: "image/jpeg",
  },
) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > options.maxWidth) {
            height = Math.round((height * options.maxWidth) / width);
            width = options.maxWidth;
          }
        } else {
          if (height > options.maxHeight) {
            width = Math.round((width * options.maxHeight) / height);
            height = options.maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("فشل ضغط الصورة"));
              return;
            }

            const compressedFile = new File(
              [blob],
              file.name.replace(/\.[^/.]+$/, ".jpg"),
              {
                type: options.mimeType,
                lastModified: Date.now(),
              },
            );

            resolve(compressedFile);
          },
          options.mimeType,
          options.quality,
        );
      };

      img.onerror = () => {
        reject(new Error("فشل تحميل الصورة"));
      };

      img.src = event.target.result;
    };

    reader.onerror = () => {
      reject(new Error("فشل قراءة الملف"));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * التحقق من حجم الصورة
 * @param {File} file - ملف الصورة
 * @param {number} maxSizeMB - الحجم الأقصى بالميجابايت
 * @returns {boolean}
 */
export const validateImageSize = (file, maxSizeMB = 5) => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
};

/**
 * التحقق من نوع الملف
 * @param {File} file - الملف المرفوع
 * @returns {boolean}
 */
export const validateImageType = (file) => {
  const validTypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/webp",
  ];
  return validTypes.includes(file.type);
};

/**
 * معالجة روابط Dropbox
 * @param {string} url - رابط Dropbox الأصلي
 * @returns {string} - رابط Dropbox المعدل
 */
export const processDropboxUrl = (url) => {
  if (!url) return url;

  if (url.includes("www.dropbox.com")) {
    return url
      .replace("www.dropbox.com", "dl.dropboxusercontent.com")
      .replace("?dl=0", "");
  }

  return url;
};

/**
 * التحقق من صحة رابط الصورة
 * @param {string} url - رابط الصورة
 * @returns {Promise<boolean>}
 */
export const validateImageUrl = async (url) => {
  try {
    const response = await fetch(url, { method: "HEAD" });
    const contentType = response.headers.get("content-type");
    return contentType && contentType.startsWith("image/");
  } catch (error) {
    console.error("URL validation error:", error);
    return false;
  }
};

/**
 * الحصول على حجم الملف بشكل قابل للقراءة
 * @param {number} bytes - حجم الملف بالبايت
 * @returns {string}
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};
